import React from 'react';
import { Badge, Box, Flex, HStack, Text } from '@chakra-ui/react';
import { Competitor, TennisGamePhase, TennisScore } from '../../types/types';
import {
  gameLog, isTiebreak, latestScore, nextServer, playerPair, pointCalls, pointSituation, setsToWin,
} from '../../utils/tennisScore';
import { BoardPlayer, boardPlayers, boardSets } from './tennisBoard';
import TennisServeBall from './TennisServeBall';

interface TennisScoreSummaryProps {
  tennisScores: TennisScore[];
  firstCompetitor: Competitor;
  secondCompetitor: Competitor;
  /** The competition's rule-set code: sets are shown only when more than one wins the match. */
  ruleSet?: string;
}

const PHASE_BADGES: Partial<Record<TennisGamePhase, { label: string; colorScheme: string }>> = {
  PREMATCH: { label: 'Prematch', colorScheme: 'gray' },
  MATCH_END: { label: 'Finished', colorScheme: 'purple' },
  CANCELLED: { label: 'Cancelled', colorScheme: 'red' },
};

/**
 * The tennis score in one line for the page header — the only scoreboard on the page. In play: games
 * in the set and the point per player, the point red when it can decide a game against the serve, the
 * set or the match, and the ball by the next server. Once finished: the games of every set, the
 * tiebreak loser's points raised.
 */
export const TennisScoreSummary: React.FC<TennisScoreSummaryProps> = ({
  tennisScores,
  firstCompetitor,
  secondCompetitor,
  ruleSet
}) => {
  const latest = latestScore(tennisScores);
  const pair = playerPair(latest, firstCompetitor.id, secondCompetitor.id);
  if (!latest || !pair) {
    return null;
  }

  const players = boardPlayers(firstCompetitor, secondCompetitor);
  const matchSetsToWin = setsToWin(ruleSet);
  const finished = latest.gamePhase === 'MATCH_END';
  const inPlay = latest.gamePhase === 'SET_IN_PROGRESS';
  const tiebreak = isTiebreak(pair.first.gamesWonCurrentSet, pair.second.gamesWonCurrentSet);
  const calls = pointCalls(pair.first.currentGamePoints, pair.second.currentGamePoints, tiebreak);
  const server = inPlay ? nextServer(tennisScores, firstCompetitor.id, secondCompetitor.id) : undefined;
  const situation = server !== undefined ? pointSituation(pair, server, matchSetsToWin) : undefined;
  const pointIsBig = !!situation
    && (situation.breakPoints > 0 || situation.setPointFor !== undefined || situation.matchPointFor !== undefined);
  const sets = finished
    ? boardSets(latest, gameLog(tennisScores, firstCompetitor.id, secondCompetitor.id, matchSetsToWin),
      firstCompetitor.id, secondCompetitor.id)
    : [];
  const phaseBadge = PHASE_BADGES[latest.gamePhase];
  const showSetsWon = matchSetsToWin !== 1 && !finished;

  const side = (player: BoardPlayer) => {
    const score = pair[player.side];
    return (
      <HStack spacing={1.5}>
        <Box w="3px" h="16px" borderRadius="sm" bg={`${player.scheme}.500`} flexShrink={0} />
        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{player.competitor.name}</Text>
        {server === player.competitor.id && <TennisServeBall />}
        {finished ? sets.map(set => {
          const won = set.winnerCompetitorId === player.competitor.id;
          return (
            <Text key={set.set} fontSize="md" fontWeight={won ? 'bold' : 'normal'} color={won ? 'gray.800' : 'gray.500'}>
              {set.games[player.side]}
              {set.tiebreakPoints && !won && <Text as="sup" fontSize="2xs" ml="1px">{set.tiebreakPoints[player.side]}</Text>}
            </Text>
          );
        }) : (
          <>
            {showSetsWon && <Text fontSize="sm" color="gray.500" title="Sets">[{score.setsWon}]</Text>}
            <Text fontSize="md" fontWeight="bold" title="Games in the set">{score.gamesWonCurrentSet}</Text>
            {(inPlay || latest.gamePhase === 'CANCELLED') && (
              <>
                <Text fontSize="sm" color="gray.400">·</Text>
                <Text fontSize="md" fontWeight="bold" color={pointIsBig ? 'red.600' : 'gray.800'}
                      title={tiebreak ? 'Tiebreak points' : 'Point'}>
                  {tiebreak && <Text as="span" fontSize="2xs" color="orange.600" mr={0.5}>TB</Text>}
                  {calls[player.side]}
                </Text>
              </>
            )}
          </>
        )}
      </HStack>
    );
  };

  return (
    <Flex align="center" gap={3} ml={2} flexWrap="wrap">
      {side(players[0])}
      <Text fontSize="xs" color="gray.500">vs</Text>
      {side(players[1])}
      {phaseBadge && <Badge colorScheme={phaseBadge.colorScheme} fontSize="xs">{phaseBadge.label}</Badge>}
    </Flex>
  );
};

export default TennisScoreSummary;
